import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const COLORS = {
  primary: "#3AAFA9",
  secondary: "#7BC8A4",
  heading: "#2E2E2E",
  text: "#4B5563",
  sectionTitle: "#1F7A75",
  background: "#3AAFA9",
};

const cancellationSections = [
  {
    title: "1. Service Cancellations",
    list: [
      "You can cancel a one-time service request up to 24 hours before the scheduled time without any penalty.",
      "Cancellations made within 24 hours of the scheduled service may incur a nominal cancellation fee to compensate our care partners.",
      "If a Janak care partner fails to arrive or a service cannot be delivered due to system errors, you are entitled to a full 100% refund."
    ]
  },
  {
    title: "2. Subscription Plans",
    list: [
      "If you cancel an active monthly or yearly subscription, you will continue to have access to the service until the end of your current billing cycle.",
      "We do not provide prorated refunds for mid-cycle cancellations of subscription plans.",
      "Subscription renewal payments are non-refundable once processed."
    ]
  },
  {
    title: "3. Refund Process",
    list: [
      "Eligible refunds for cancelled services will be processed back to your original method of payment.",
      "Please allow 5-7 business days for the refunded amount to reflect in your bank account or credit card statement.",
      "For failed transactions where money is debited but service is not booked, the amount will be automatically refunded by our payment gateway."
    ]
  },
  {
    title: "4. No-Show Policy",
    text:
      "If you or the elder care recipient are not available at the scheduled service location and time, the service will be considered rendered, and no refund will be issued.",
  },
  {
    title: "5. Force Majeure",
    text:
      "Janak shall not be held responsible for cancellations or delays caused by circumstances beyond our reasonable control, such as natural disasters, strikes, or severe weather. In such cases, we will assist in rescheduling the service.",
  },
  {
    title: "6. Dispute Escalation",
    text:
      "For any disputes regarding cancellations or refunds, please contact our support team within 48 hours of the scheduled service.",
  },
  {
    title: "7. Contact Information",
    text: (
      <span>
        To request a refund or raise a dispute, contact us at{" "}
        <a href="mailto:contact@thejanak.com" className="text-[#3AAFA9] hover:underline font-medium">
          contact@thejanak.com
        </a>{" "}
        or call{" "}
        <a href="tel:+919098114568" className="text-[#3AAFA9] hover:underline font-medium">
          +91 90981 14568
        </a>.
      </span>
    ),
  },
];

const CancellationRefund = () => {
  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const headingRef = useRef(null);
  const introRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
      tl.from(sectionRef.current, { opacity: 0, duration: 0.5 })
        .from(cardRef.current, { y: 36, opacity: 0, duration: 0.7 }, "-=0.2")
        .from(headingRef.current, { y: 18, opacity: 0, duration: 0.45 }, "-=0.2")
        .from(introRef.current, { y: 16, opacity: 0, duration: 0.45 }, "-=0.25")
        .from(
          ".cancellation-section-item",
          { y: 24, opacity: 0, stagger: 0.07, duration: 0.38 },
          "-=0.2",
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen overflow-hidden px-6 py-20"
      style={{ background: COLORS.background }}
    >
      <div
        className="absolute left-10 top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.primary}33` }}
      />
      <div
        className="absolute bottom-20 right-10 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.secondary}40` }}
      />

      <div
        ref={cardRef}
        className="relative mx-auto max-w-4xl rounded-3xl bg-white/80 px-6 py-12 text-gray-800 shadow-[0_30px_90px_rgba(0,0,0,0.15)] backdrop-blur-2xl md:px-12"
      >
        <h1
          ref={headingRef}
          className="mb-10 text-center text-4xl font-extrabold md:text-5xl"
          style={{ color: COLORS.heading }}
        >
          Cancellation & Refund Policy
        </h1>

        <p
          ref={introRef}
          className="mb-10 text-center text-base leading-relaxed md:text-lg"
          style={{ color: COLORS.text }}
        >
          We strive to provide seamless care services. This policy outlines how cancellations and refunds are handled for Janak App subscriptions and one-time service bookings.
        </p>

        <div className="space-y-9 text-[15.5px] leading-relaxed">
          {cancellationSections.map((section) => (
            <div key={section.title} className="cancellation-section-item">
              <h2
                className="mb-3 text-xl font-bold md:text-2xl"
                style={{ color: COLORS.sectionTitle }}
              >
                {section.title}
              </h2>
              {section.text ? <p style={{ color: COLORS.text }}>{section.text}</p> : null}
              {section.list ? (
                <ul className="list-disc space-y-1.5 pl-6" style={{ color: COLORS.text }}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>

        <p className="mt-14 text-center text-sm text-gray-500">Last updated: July 2026</p>
      </div>
    </section>
  );
};

export default CancellationRefund;
